import type { Product } from '@commerce/types/product'
import type { ProductVariant } from '@commerce/types/product';

const getFirstOpTCW = (
  product: Product,
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  },
  typeSelected: string,
  currentFilters: string[]
) => {
  const filterByMetal = product?.variants
    ?.map((vr) => vr.options)
    .filter((vrn) => {
      const vn = vrn.filter((v) => v.displayName === 'Metal')[0]
      return vn ? vn.values[0].label === variantSettings.metal : false
    })

  const filterByType = currentFilters.includes('diam_type')
    ? filterByMetal.filter((vrn) => {
        const vn = vrn.filter((v) => v.displayName === 'Diamond Type')[0]
        return vn ? vn.values[0].label === typeSelected : false
      })
    : filterByMetal

  return Array.from(
    new Set(
      filterByType.map((vnt) => {
        const vn = vnt.filter((v) => v.displayName === 'Total Carat Weight')[0]
        return vn ? vn.values[0].label : ''
      })
    )
  ).filter((v) => v !== '')[0]
}

const getFirstOpSize = (
  product: Product,
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  },
  typeSelected: string,
  currentFilters: string[]
) => {
  const firstOpTCW = getFirstOpTCW(
    product,
    variantSettings,
    typeSelected,
    currentFilters
  )
  const filterByMetal = product?.variants
    ?.map((vr) => vr.options)
    .filter((vrn) => {
      const vn = vrn.filter((v) => v.displayName === 'Metal')[0]
      return vn ? vn.values[0].label === variantSettings.metal : false
    })

  const filterByType = currentFilters.includes('diam_type')
    ? filterByMetal.filter((vrn) => {
        const vn = vrn.filter((v) => v.displayName === 'Diamond Type')[0]
        return vn ? vn.values[0].label === typeSelected : false
      })
    : filterByMetal

  const filterByTCW = currentFilters.includes('tcw')
    ? filterByType.filter((vrn) => {
        const vn = vrn.filter((v) => v.displayName === 'Total Carat Weight')[0]
        return vn ? vn.values[0].label === firstOpTCW : false
      })
    : filterByType

  return Array.from(
    new Set(
      filterByTCW.map((vnt) => {
        const vn = vnt.filter((v) => v.displayName === 'Size')[0]
        return vn ? vn.values[0].label : ''
      })
    )
  ).filter((v) => v !== '')[0]
}

const getValidOptionsForMetal = (
  metal: string, 
  diamondType: string, 
  variants: ProductVariant[], 
  optionKey: string
): string[] => {
  // Filter variants that match both metal and diamond type
  const filteredVariants = variants.filter(variant => {
    const metalOption = variant.options.find(option => option.displayName.toLowerCase() === 'metal');
    const diamondTypeOption = variant.options.find(option => option.displayName.toLowerCase() === 'diamond type');
    
    return (
      metalOption && metalOption.values[0].label.toLowerCase() === metal.toLowerCase() &&
      diamondTypeOption && diamondTypeOption.values[0].label.toLowerCase() === diamondType.toLowerCase()
    );
  });

  // Get valid options for the specified key (e.g., 'tcw')
  const validOptions = filteredVariants
    .map(variant => {
      const option = variant.options.find(option => option.displayName.toLowerCase() === optionKey.toLowerCase());
      return option ? option.values[0].label : null;  // Return null if there is no value
    })
    .filter(option => option !== null && option !== undefined); // Filters null and undefined values

  // Remove duplicates and return the resulting array
  return Array.from(new Set(validOptions)) as string[];
}


export { getFirstOpTCW, getFirstOpSize, getValidOptionsForMetal }
